import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Mail, Lock, User, Loader2, CheckCircle2, Gift } from "lucide-react";
import { GoogleSignInButton, OrDivider } from "@/components/GoogleSignInButton";
import { BrandLogo } from "@/components/BrandLogo";
import { trackEvent } from "@/lib/analytics";
import { carrySnapshotIfAny } from "@/lib/snapshotCarryover";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignupPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [params] = useSearchParams();
  const refCode = (params.get("ref") || "").toUpperCase();
  const [form, setForm] = useState(() => {
    let email = "", name = "";
    try {
      email = localStorage.getItem("passadc_lead_email") || "";
      name = localStorage.getItem("passadc_lead_name") || "";
    } catch {}
    return { name, email, password: "" };
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [referrer, setReferrer] = useState(null);

  useEffect(() => {
    // Remember a "continue to checkout after signup" intent (survives Google OAuth).
    const checkout = params.get("checkout");
    if (checkout) { try { localStorage.setItem("passadc_pending_checkout", checkout); } catch { /* ignore */ } }
  }, [params]);

  useEffect(() => {
    if (!refCode) return;
    axios.post(`${API}/referrals/track-visit`, { code: refCode })
      .then((r) => { if (r.data.valid) setReferrer(r.data); })
      .catch(() => {});
  }, [refCode]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await register(form.email.trim().toLowerCase(), form.password, form.name.trim());
      trackEvent("signup_completed", { method: "email", user_id: user?.id });
      // Carry the readiness snapshot into the new account (skips /welcome step 1)
      await carrySnapshotIfAny();
      // Came in from a pricing CTA -> continue straight to that plan's checkout
      const checkout = params.get("checkout") || (() => { try { return localStorage.getItem("passadc_pending_checkout"); } catch { return null; } })();
      if (checkout) {
        try { localStorage.removeItem("passadc_pending_checkout"); } catch { /* ignore */ }
        navigate(`/billing?plan=${checkout}`, { replace: true });
        return;
      }
      // From the readiness results "Start your 10-minute plan" CTA -> go straight to the plan
      const planSubject = params.get("plan_subject") || localStorage.getItem("passadc_plan_subject");
      if (planSubject) {
        try { localStorage.removeItem("passadc_plan_subject"); } catch { /* ignore */ }
        navigate(`/plan/today?subject=${planSubject}`, { replace: true });
        return;
      }
      // If user came in from the diagnostic flow, send them back to see their results
      const hasPending = !!localStorage.getItem("adcprep_pending_diagnostic");
      navigate(hasPending ? "/diagnostic" : "/welcome", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not create account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <BrandLogo size={28} />
            <span className="font-bold text-lg">PassADC</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Start your free account</h1>
          <p className="text-muted-foreground text-sm">No credit card. Instant access.</p>
          {referrer && (
            <div data-testid="referral-banner" className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-accent/15 text-accent border border-accent/30 text-xs font-medium">
              <Gift className="w-3.5 h-3.5" />
              {referrer.referrer_name} invited you - enjoy a {referrer.referee_trial_days}-day free Pro trial 🎉
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="signup-form"
          className="glass rounded-2xl p-6 md:p-8 border border-border space-y-5"
        >
          {error && (
            <div data-testid="signup-error" className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-destructive">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Your name</label>
            <div className="relative">
              <User className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="signup-name"
                type="text"
                value={form.name}
                onChange={update("name")}
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-secondary border border-border text-sm focus:outline-none focus:border-primary/50"
                placeholder="Dr. Smith"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="signup-email"
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={update("email")}
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-secondary border border-border text-sm focus:outline-none focus:border-primary/50"
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="signup-password"
                type="password"
                required
                minLength={6}
                autoComplete="new-password"
                value={form.password}
                onChange={update("password")}
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-secondary border border-border text-sm focus:outline-none focus:border-primary/50"
                placeholder="At least 6 characters"
              />
            </div>
          </div>

          <div className="space-y-2 py-2 text-xs text-muted-foreground">
            {[
              "7 days of full Pro access - free, no card",
              "All 1,200+ exam-style MCQs, full mocks + AI tutor unlocked",
              "We find your weakest subject in 3 minutes",
              "Free plan afterwards - keep studying at $0",
            ].map((t) => (
              <div key={t} className="flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                <span>{t}</span>
              </div>
            ))}
          </div>

          <Button
            data-testid="signup-submit"
            type="submit"
            disabled={loading}
            className="w-full rounded-full py-6 text-base font-semibold"
          >
            {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account...</>) : "Create free account"}
          </Button>

          <OrDivider />
          <GoogleSignInButton label="Sign up with Google" />
        </form>

        <p className="text-center text-sm mt-6 text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="signup-to-login">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
